/* eslint-disable max-lines */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import isEqual from "lodash/isEqual";
import {analyticsSendEvent, getUTMParams, toEventData} from "../../libs/analytics";
import {isAvatarAction, isChoiceUserInputAction, isSharedId, isSystemAction} from "../../types";
import {reverse} from "../../routing";
import browser, {isIPhone} from "../../browser";
import {AudioPlayback} from "../../libs/audio";
import {errorsAudioQuery, ErrorsAudioQueryData, query, QueryData} from "./player.graphql";
import {
  Avatar,
  AvatarAction,
  ChoiceUserInputAction,
  ErrorAudio,
  Challenge,
  ChallengeDisplayMode,
  ChallengeInputMode,
  QueryGetErrorsAudioArgs,
  QueryGetChallengeArgs,
  Scalars,
  SystemAction,
  QueryGetAdaptiveInputArgs,
  ChallengeMode,
} from "../../schema";
import {bindInterval, ExternalConnectionType, getExternalConnectionType} from "../../utils";
import {useSendBugReport} from "../../hooks/telemetry";
import {BACKEND} from "../../settings";

// Hooks
import {useDispatch, useSelector} from "react-redux";
import {useHistory, useParams} from "react-router";
import {useTranslation} from "react-i18next";
import {useCurrentUser} from "../../App.context";
import {useAudio} from "../../providers/audio";
import {useAudioPreloader} from "../../providers/audioPreloader";
import {useLazyQuery, useQuery} from "@apollo/client";

// Components
import PlayerChallengeNotAvailableScene from "./challenge-not-available";
import MediaRecorderProvider from "../../providers/mediaRecorder";
import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import PlayerResultsScreen from "./player.results";
import WithProblemsWidget from "../../components/system/ProblemsWidget";
import WithNetworkWidget from "../../components/system/NetworkWidget";
import ChallengeResumeWidget from "../../components/player/ChallengeResumeWidget";

// Players (skins)
import VideoCallPlayer from "../../components/player/video-call/VideoCallPlayer";
import ModernPlayer from "../../components/player/modern/ModernPlayer";
import BabylonAvatarPlayerLazy from "../../components/player/BabylonAvatarPlayerLazy"; // debug Babylon mode

// Redux
import PlayerReduxStoreProvider from "../../redux/player";
import {PlayerReduxState, PlayerStage} from "../../redux/player/types";
import {getSessionId, isStartFailed, isResumeFailed, getCurrentAction} from "../../redux/player/selectors";
import useFullscreenMode from "../../hooks/useFullscreenMode";
import {useProblemsWatcher} from "../../providers/problemsWatcher";
import {AdaptiveInputQueryData, getAdaptiveInputQuery} from "../../redux/player/graphql";
import {start, syncAdaptiveInput, resume} from "../../redux/player/actions";
import {useTryInviteUserToChallengeMutation} from "../login/login.graphql";
import {useSelectAccountMutation} from "../login/login-select-account.graphql";

export default function PlayerPlayerScene() {
  const {t} = useTranslation();
  const {id: challengeId} = useParams<{id: string}>();
  const {isDebug, isShared, isPreview, isExternal, noTrackFocus, externalUserId, utm} = useMemo(() => {
    const params = new URLSearchParams(window.location.search);

    return {
      isDebug: params.get("debug") === "1",
      isPreview: params.get("preview") === "1",
      isExternal: params.get("external") === "1",
      isShared: isSharedId(challengeId),
      noTrackFocus: params.get("noTrackFocus") === "1",
      externalUserId: params.get("externalUserId") ?? null,
      utm: getUTMParams(params),
    };
  }, [challengeId]);
  const isDeveloper = !!useCurrentUser()?.isDeveloper;

  const {audioContext, audioManager} = useAudio();
  const {audioPreloadManager, onPreloadReady: onAudioPreloadReady} = useAudioPreloader();

  useEffect(() => {
    const playbackOpts = {
      preload: true,
      onTrackStatusUpdate: audioPreloadManager.updateTrackStatus,
      onPreloadReady: onAudioPreloadReady,
      onPlayError: ({src}: HTMLAudioElement) => audioPreloadManager.stopTrackingStatus(src),
    };
    audioManager.installPlayback("player/avatar", new AudioPlayback(audioContext, playbackOpts));
    audioManager.installPlayback("player/system", new AudioPlayback(audioContext, playbackOpts));

    return () => {
      audioManager.uninstallPlayback("player/avatar");
      audioManager.uninstallPlayback("player/system");
    };
  }, [audioContext, audioManager, audioPreloadManager, onAudioPreloadReady]);

  const playerMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = (params.get("mode") || "auto").toLowerCase();

    switch (mode) {
      case "3d":
        return "3D";
      case "2d":
        return "2D";
      default:
        if (
          browser.satisfiesOS({
            android: "<8",
          })
        ) {
          return "2D";
        }
        return browser.satisfies({
          chrome: ">=78",
          firefox: ">=78",
          safari: ">=13",
          ios: ">=13",
          edge: ">=84",
        })
          ? "3D"
          : "2D";
    }
  }, []);

  useEffect(() => {
    analyticsSendEvent("playerPlay", {
      challengeId,
      isShared,
      isPreview,
      isExternal,
      initialMode: playerMode,
      externalUserId: externalUserId ?? undefined,
    });
  }, [challengeId, isShared, isPreview, isExternal, playerMode, externalUserId]);

  const {data} = useQuery<QueryData, QueryGetChallengeArgs>(query, {
    fetchPolicy: "network-only",
    variables: {
      challengeId: challengeId,
      externalUserId: externalUserId ?? null,
    },
  });

  const loaded = !!data;
  const challenge = data?.challenge ?? null;
  const scenarios = data?.scenarios ?? null;
  const avatar = challenge?.avatar ?? null;
  const gestures = challenge?.gestures;
  const avatarVoiceId = challenge?.avatarVoiceId;
  const isVoiceIdValid = !!avatarVoiceId;

  const errorsAudioData = useQuery<ErrorsAudioQueryData, QueryGetErrorsAudioArgs>(errorsAudioQuery, {
    fetchPolicy: "network-only",
    variables: {
      voice: (isVoiceIdValid ? avatarVoiceId : "") as string,
    },
    skip: !challenge?.avatarVoiceId,
  });

  const sortedScenarios = useMemo(() => {
    if (scenarios) {
      const sorted = [...scenarios].sort((a, b) => Number(b.isMain) - Number(a.isMain));
      sorted.forEach((scenario, index) => {
        scenario.index = index;
        if (scenario.nextScenario) {
          const nextScenarioIndex = sorted.findIndex(({id}) => id === scenario.nextScenario);
          if (nextScenarioIndex !== -1) {
            scenario.nextScenarioIndex = nextScenarioIndex;
          }
        }
      });
      return sorted;
    }
    return null;
  }, [scenarios]);

  const errorsAudio = errorsAudioData.data?.audios;

  const notUsePreload = isIPhone();

  useEffect(() => {
    if (!sortedScenarios || notUsePreload) {
      return;
    }

    const actions = sortedScenarios.flatMap(({actions}) => actions);

    const choices = actions.filter((action): action is ChoiceUserInputAction => isChoiceUserInputAction(action));

    const choicesWithAudio = choices
      .flatMap((action) =>
        action.branches.flatMap((branch) => [branch.avatarReaction?.audio ?? "", branch.systemReaction?.audio ?? ""]),
      )
      .filter((str) => str !== "");

    const actionsWithAudio = actions
      .filter((action): action is AvatarAction | SystemAction => isAvatarAction(action) || isSystemAction(action))
      .map(({audio}) => audio ?? "");

    audioPreloadManager.preload([...actionsWithAudio, ...choicesWithAudio], true);
  }, [sortedScenarios, notUsePreload, audioManager, audioPreloadManager]);

  if (loaded && !challenge) {
    return <PlayerChallengeNotAvailableScene reason="not-found" />;
  }

  if (loaded && isDebug && isDeveloper) {
    return <BabylonAvatarPlayerLazy debug avatar={avatar!} gestures={gestures!} />;
  }

  return (
    <>
      <ReactDocumentTitle title={challenge?.name ?? t("player.title")} />
      {challenge && sortedScenarios && (
        <MediaRecorderProvider
          mediaType={challenge.displayMode === ChallengeDisplayMode.VIDEO_CALL ? "video" : "audio"}
          noTrackFocus={noTrackFocus}
          challenge={challenge}
          isShared={isShared}
        >
          <PlayerReduxStoreProvider
            challengeId={challengeId}
            challenge={challenge}
            scenarios={sortedScenarios}
            isPreview={isPreview}
            isShared={isShared}
            isExternal={isExternal}
            externalUserId={externalUserId}
            utm={utm}
          >
            <ThemedPlayer
              challengeId={challengeId}
              challenge={challenge}
              avatar={avatar!}
              gestures={gestures!}
              initialMode={playerMode}
              isPreview={isPreview}
              isExternal={isExternal}
              externalUserId={externalUserId}
              errorsAudio={errorsAudio}
            />
          </PlayerReduxStoreProvider>
        </MediaRecorderProvider>
      )}
    </>
  );
}

type PlayerInnerProps = {
  challengeId: Scalars["ID"];
  challenge: Challenge;
  avatar: Avatar;
  gestures: string[];
  initialMode: "2D" | "3D";
  isExternal: boolean;
  isPreview: boolean;
  externalUserId: Scalars["ID"] | null;
  errorsAudio?: ErrorAudio[];
};

function ThemedPlayer({
  challengeId,
  challenge,
  avatar,
  gestures,
  initialMode,
  isExternal,
  isPreview,
  externalUserId,
  errorsAudio,
}: PlayerInnerProps) {
  const history = useHistory();
  const dispatch = useDispatch();

  const [allowFullscreen, toggleFullscreen] = useFullscreenMode(
    challenge.inputMode === ChallengeInputMode.TEXT ? {} : {hotkey: "KeyF"},
  );
  const {failed, resumeFailed, stage, sessionId, actionId, adaptiveInputId, stopPollingGetAdaptiveResponses} =
    useSelector(ThemedPlayer.selector, isEqual);

  const [resumeWidgetVisibility, setResumeWidgetVisibility] = useState(challenge.canResume && !isPreview);
  const [applyInviteUserToChallengeMutation] = useTryInviteUserToChallengeMutation();
  const [selectAccount] = useSelectAccountMutation();
  const inviteByLink = challenge?.inviteByLink;
  const accessKey = challenge?.accessToken;
  const accountId = challenge?.accountId;
  const loggedIn = !!useCurrentUser();

  const onRestart = useCallback(() => {
    dispatch(start());
    setResumeWidgetVisibility(false);
  }, [dispatch]);

  const onResume = useCallback(() => {
    dispatch(resume());
    setResumeWidgetVisibility(false);
  }, [dispatch]);

  useEffect(() => {
    if (!(challenge.canResume && !isPreview)) {
      if (inviteByLink && loggedIn && !isPreview) {
        applyInviteUserToChallengeMutation({
          variables: {
            challengeId: challenge.id,
            accessKey: accessKey,
          },
        }).then((success) => {
          if (success) {
            selectAccount({variables: {accountId: accountId}}).then(() => {
              history.push(reverse("playerPlay", {id: challenge.id}));
              dispatch(start());
            });
          }
        });
      } else {
        dispatch(start());
      }
    }
  }, [
    dispatch,
    challenge.canResume,
    isPreview,
    inviteByLink,
    loggedIn,
    accessKey,
    accountId,
    applyInviteUserToChallengeMutation,
    selectAccount,
    history,
    challenge.id,
  ]);

  const backUrl = useMemo(
    () => (isPreview ? reverse("editorChallengeEdit", {id: challengeId}) : reverse("player", {id: challengeId})),
    [challengeId, isPreview],
  );

  const {resetAllFailReasons, isFailed, failReasons} = useProblemsWatcher();

  const sendBugReport = useSendBugReport();
  const onBugReportSend = useCallback(
    (message: string, attachment?: File, contact?: string) => {
      resetAllFailReasons();
      return sendBugReport({
        message,
        attachment,
        contact,
        sessionId,
        location: "playerProblemsWatcher",
        errorReason: failReasons.join("; "),
        actionId,
      });
    },
    [resetAllFailReasons, sendBugReport, sessionId, actionId, failReasons],
  );

  const [ping, setPing] = useState<number | null>(null);
  const [isPingChecked, setPingChecked] = useState<boolean>(false);
  const [networkWidgetClosed, setNetworkWidgetClosed] = useState<boolean>(false);
  const serverUrl = BACKEND;

  const checkPing = async () => {
    const startTime = Date.now();
    try {
      const response = await fetch(serverUrl, {method: "GET", cache: "no-cache"});
      const endTime = Date.now();
      if (response.ok) {
        const latency = endTime - startTime;
        setPing(latency);
        setPingChecked(true);
      }
    } catch (err) {
      console.warn("Ping error");
    }
  };

  const closeNetworkWidget = useCallback(() => {
    setNetworkWidgetClosed(true);
  }, []);

  useEffect(() => {
    if (stage === PlayerStage.Result) {
      analyticsSendEvent(
        "playerFinish",
        toEventData({
          sessionId: sessionId,
          isExternal,
          externalConnectionType: getExternalConnectionType(),
        }),
      );
    }
    if (!isPingChecked) {
      checkPing && checkPing();
    }
  });

  const [adaptiveInputQuery] = useLazyQuery<AdaptiveInputQueryData, QueryGetAdaptiveInputArgs>(getAdaptiveInputQuery);

  useEffect(() => {
    const isAdaptiveMode =
      challenge.mode === ChallengeMode.COMPETENCE_TEST || challenge.mode === ChallengeMode.RPG_MODE;
    if (!adaptiveInputId || !sessionId || stopPollingGetAdaptiveResponses) {
      return;
    }

    return bindInterval(() => {
      if (isAdaptiveMode && adaptiveInputId && sessionId) {
        adaptiveInputQuery({
          variables: {
            sessionId: sessionId!,
            inputId: adaptiveInputId!,
          },
        }).then((result) => {
          dispatch(syncAdaptiveInput(result.data?.input));
        });
      }
    }, 300);
  }, [dispatch, challenge.mode, adaptiveInputQuery, sessionId, adaptiveInputId, stopPollingGetAdaptiveResponses]);

  if (inviteByLink && !loggedIn) {
    history.push(reverse("login") + "?_invited=" + challenge.id + "&_access_key=" + accessKey + "&_aid=" + accountId);
  }

  if (failed) {
    return <PlayerChallengeNotAvailableScene reason="limits" />;
  }

  if (resumeFailed) {
    return <PlayerChallengeNotAvailableScene reason="expired" />;
  }

  if (stage === PlayerStage.Result) {
    if (isExternal && getExternalConnectionType() === ExternalConnectionType.IFRAME) {
      const params = new URLSearchParams();

      externalUserId && params.set("externalUserId", externalUserId);
      history.push(reverse("lmsPlay", {id: challengeId}) + `?${params}`);
    }
    return <PlayerResultsScreen headless={isExternal} challenge={challenge} />;
  }

  const playerDefaultProps = {
    allowFullscreen,
    toggleFullscreen,
    challenge,
    avatar,
    gestures,
    initialMode,
    headless: isExternal,
    backUrl,
    errorsAudio,
  };

  switch (challenge.displayMode) {
    case ChallengeDisplayMode.VIDEO_CALL:
      return (
        <WithProblemsWidget onBugReportSend={onBugReportSend} showWidget={isFailed} onClose={resetAllFailReasons}>
          <WithNetworkWidget showWidget={!!ping && ping > 1000 && !networkWidgetClosed} onClose={closeNetworkWidget}>
            {resumeWidgetVisibility && <ChallengeResumeWidget onRestart={onRestart} onResume={onResume} />}
            <VideoCallPlayer {...playerDefaultProps} />
          </WithNetworkWidget>
        </WithProblemsWidget>
      );
    default:
      console.warn("PlayerScene: unsupported player display mode");
    // eslint-disable-next-line no-fallthrough
    case ChallengeDisplayMode.DEFAULT:
      return (
        <WithProblemsWidget onBugReportSend={onBugReportSend} showWidget={isFailed} onClose={resetAllFailReasons}>
          <WithNetworkWidget showWidget={!!ping && ping > 1000 && !networkWidgetClosed} onClose={closeNetworkWidget}>
            {resumeWidgetVisibility && <ChallengeResumeWidget onRestart={onRestart} onResume={onResume} />}
            <ModernPlayer {...playerDefaultProps} />
          </WithNetworkWidget>
        </WithProblemsWidget>
      );
  }
}

ThemedPlayer.selector = (state: PlayerReduxState) => {
  const adaptiveInput = state.adaptiveInput;
  return {
    failed: isStartFailed(state),
    resumeFailed: isResumeFailed(state),
    stage: state.stage,
    sessionId: getSessionId(state),
    actionId: getCurrentAction(state)?.id,
    adaptiveInputId: adaptiveInput?.id,
    stopPollingGetAdaptiveResponses: adaptiveInput?.isClosed,
  };
};
